import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@heroui/react'
import {
  useCreateSuggestion,
  useDeleteSuggestion,
  useSuggestions,
  useUpdateSuggestion,
} from 'hooks/useSuggestions'
import {
  AlertCircle,
  Calendar,
  Edit3,
  FileText,
  Loader2,
  MessageSquare,
  Plus,
  Search,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import SuggestionModal from '../../components/KnowledgeBases/SuggestionModal'

export default function Suggestions({ knowledgeBases }) {
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [searchFilter, setSearchFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [editingSuggestion, setEditingSuggestion] = useState(null)
  const [newSuggestion, setNewSuggestion] = useState({
    question: '',
    answer: '',
    type: 'SUGGESTION',
  })
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  // Fetch suggestions for selected document
  const {
    data: suggestionsData,
    isLoading,
    error,
    refetch,
  } = useSuggestions(selectedDocument?.id, {
    type: typeFilter,
  }, {
    enabled: !!selectedDocument?.id,
  })

  // Mutations
  const createMutation = useCreateSuggestion({
    onSuccess: () => {
      setNewSuggestion({ question: '', answer: '', type: 'SUGGESTION' })
      onAddClose()
      // Refetch the suggestions data to ensure immediate update
      refetch()
    },
    onError: (error) => {
      console.error('Failed to create suggestion:', error)
    },
  })

  const updateMutation = useUpdateSuggestion({
    onSuccess: () => {
      setEditingSuggestion(null)
      onEditClose()
      // Refetch the suggestions data to ensure immediate update
      refetch()
    },
    onError: (error) => {
      console.error('Failed to update suggestion:', error)
    },
  })

  const deleteMutation = useDeleteSuggestion({
    onSuccess: () => {
      // Refetch the suggestions data to ensure immediate update
      refetch()
    },
    onError: (error) => {
      console.error('Failed to delete suggestion:', error)
    },
  })

  // Filter suggestions by search term (client-side filtering)
  const filteredSuggestions = useMemo(() => {
    if (!suggestionsData?.data?.suggestions)
      return []

    if (!searchFilter)
      return suggestionsData.data.suggestions

    return suggestionsData.data.suggestions.filter(qa =>
      qa.question.toLowerCase().includes(searchFilter.toLowerCase())
      || qa.answer.toLowerCase().includes(searchFilter.toLowerCase()),
    )
  }, [suggestionsData?.data?.suggestions, searchFilter])

  // Get suggestion count for each document
  const getDocumentSuggestionCount = (docId) => {
    // This would ideally come from a separate API call or be cached
    // For now, only show count for selected document
    if (selectedDocument?.id === docId) {
      return suggestionsData?.data?.total || 0
    }
    return 0
  }

  const handleAddSuggestion = () => {
    if (!selectedDocument || !newSuggestion.question.trim() || !newSuggestion.answer.trim()) {
      return
    }

    createMutation.mutate({
      knowledgeBaseId: selectedDocument.id,
      data: {
        question: newSuggestion.question.trim(),
        answer: newSuggestion.answer.trim(),
        type: newSuggestion.type,
      },
    })
  }

  const handleEditSuggestion = () => {
    if (!editingSuggestion || !editingSuggestion.question.trim() || !editingSuggestion.answer.trim()) {
      return
    }

    updateMutation.mutate({
      qaPairId: editingSuggestion.id,
      data: {
        question: editingSuggestion.question.trim(),
        answer: editingSuggestion.answer.trim(),
      },
    })
  }

  const handleDeleteSuggestion = (qaPair) => {
    setDeleteConfirm(qaPair)
    onDeleteOpen()
  }

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id)
      setDeleteConfirm(null)
      onDeleteClose()
    }
  }

  const openEditModal = (suggestion) => {
    setEditingSuggestion({ ...suggestion })
    onEditOpen()
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'SUGGESTION':
        return 'primary'
      case 'FEEDBACK':
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <div className="flex h-full gap-4">
      {/* Document Selector - Left Sidebar */}
      <div className="flex w-80 flex-col rounded-lg border bg-white shadow-sm">
        <div className="border-b p-4">
          <h3 className="text-lg font-semibold text-gray-900">Select Document</h3>
          <p className="mt-1 text-sm text-gray-600">Choose a document to manage its suggestions</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {knowledgeBases?.vectors?.length === 0
            ? (
                <div className="mt-8 text-center text-gray-500">
                  <FileText className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                  <p>No documents available</p>
                </div>
              )
            : (
                <div className="space-y-2">
                  {knowledgeBases.vectors.map((doc) => {
                    const docSuggestionCount = getDocumentSuggestionCount(doc.id)
                    return (
                      <button
                        type="button"
                        key={doc.id}
                        onClick={() => setSelectedDocument(doc)}
                        className={`
                          w-full rounded border p-3 text-left transition-colors
                          ${
                      selectedDocument?.id === doc.id
                        ? 'border-blue-200 bg-blue-50'
                        : `
                          border-gray-200 bg-gray-50
                          hover:bg-gray-100
                        `
                      }
                        `}
                      >
                        <div className="flex items-start gap-3">
                          <FileText className="mt-0.5 h-5 w-5 text-gray-500" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-gray-900">
                              {doc.name || `Document ${doc.id}`}
                            </div>
                            {doc.description && (
                              <div className={`
                                mt-1 truncate text-sm text-gray-600
                              `}
                              >
                                {doc.description}
                              </div>
                            )}
                            <div className="mt-1 flex items-center gap-2">
                              <MessageSquare className="h-3 w-3 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {selectedDocument?.id === doc.id
                                  ? (
                                      isLoading ? 'Loading...' : `${docSuggestionCount} suggestion${docSuggestionCount !== 1 ? 's' : ''}`
                                    )
                                  : (
                                      'Select to view'
                                    )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col rounded-lg border bg-white shadow-sm">
        {/* Header */}
        <div className="border-b p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Q&A Suggestions</h3>
              {selectedDocument
                ? (
                    <p className="mt-1 text-sm text-gray-600">
                      Managing suggestions for:
                      {' '}
                      {selectedDocument.name || `Document ${selectedDocument.id}`}
                    </p>
                  )
                : (
                    <p className="mt-1 text-sm text-gray-600">Select a document to manage its suggestions</p>
                  )}
            </div>
            <div className="flex items-center gap-3">
              {selectedDocument && suggestionsData && (
                <Chip size="sm" variant="flat" color="primary">
                  {filteredSuggestions.length}
                  {' '}
                  suggestion
                  {filteredSuggestions.length !== 1 ? 's' : ''}
                </Chip>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col">
          {!selectedDocument
            ? (
                <div className={`
                  flex flex-1 items-center justify-center text-gray-500
                `}
                >
                  <div className="text-center">
                    <MessageSquare className={`
                      mx-auto mb-4 h-16 w-16 text-gray-300
                    `}
                    />
                    <h3 className="mb-2 text-lg font-medium">Select a Document</h3>
                    <p className="max-w-sm text-center text-sm">
                      Choose a document from your knowledge base to manage its Q&A suggestions.
                    </p>
                  </div>
                </div>
              )
            : error
              ? (
                  <div className={`
                    flex flex-1 items-center justify-center text-red-500
                  `}
                  >
                    <div className="text-center">
                      <AlertCircle className={`
                        mx-auto mb-4 h-16 w-16 text-red-300
                      `}
                      />
                      <h3 className="mb-2 text-lg font-medium">Error Loading Suggestions</h3>
                      <p className="mb-4 max-w-sm text-center text-sm">
                        {error.message || 'Failed to load suggestions'}
                      </p>
                      <Button onClick={() => refetch()} variant="bordered" size="sm">
                        Try Again
                      </Button>
                    </div>
                  </div>
                )
              : (
                  <>
                    {/* Search and Filter Bar */}
                    <div className="border-b p-4">
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <Search className={`
                            absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2
                            text-gray-400
                          `}
                          />
                          <Input
                            placeholder="Search questions or answers..."
                            className="pl-10"
                            value={searchFilter}
                            onChange={e => setSearchFilter(e.target.value)}
                            radius="none"
                          />
                        </div>
                        <Select
                          placeholder="Filter by type"
                          selectedKeys={[typeFilter]}
                          onSelectionChange={keys => setTypeFilter(Array.from(keys)[0] || 'ALL')}
                          className="w-40"
                          radius="none"
                        >
                          <SelectItem key="ALL">All Types</SelectItem>
                          <SelectItem key="SUGGESTION">Suggestions</SelectItem>
                          <SelectItem key="FEEDBACK">Feedback</SelectItem>
                        </Select>
                        <Button
                          color="primary"
                          onPress={onAddOpen}
                          radius="none"
                          className="flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Suggestion
                        </Button>
                      </div>
                    </div>

                    {/* Suggestions Content */}
                    <div className="flex-1 overflow-y-auto">
                      {isLoading
                        ? (
                            <div className={`
                              flex h-full items-center justify-center
                            `}
                            >
                              <div className={`
                                flex items-center gap-2 text-gray-500
                              `}
                              >
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span>Loading suggestions...</span>
                              </div>
                            </div>
                          )
                        : filteredSuggestions.length === 0
                          ? (
                              <div className={`
                                flex h-full items-center justify-center
                                text-gray-500
                              `}
                              >
                                <div className="text-center">
                                  <MessageSquare className={`
                                    mx-auto mb-3 h-12 w-12 text-gray-300
                                  `}
                                  />
                                  <p className="text-sm">
                                    {searchFilter ? 'No suggestions match your search' : 'No suggestions yet'}
                                  </p>
                                  {!searchFilter && (
                                    <Button
                                      variant="light"
                                      onPress={onAddOpen}
                                      className="mt-2"
                                      size="sm"
                                    >
                                      Add your first suggestion
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )
                          : (
                              <Table aria-label="Suggestions table" removeWrapper>
                                <TableHeader>
                                  <TableColumn>QUESTION</TableColumn>
                                  <TableColumn>ANSWER</TableColumn>
                                  <TableColumn>TYPE</TableColumn>
                                  <TableColumn>UPDATED</TableColumn>
                                  <TableColumn width={100}>ACTIONS</TableColumn>
                                </TableHeader>
                                <TableBody>
                                  {filteredSuggestions.map(qa => (
                                    <TableRow key={qa.id}>
                                      <TableCell>
                                        <div className="max-w-xs">
                                          <p className="text-sm font-medium">{qa.question}</p>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="max-w-md">
                                          <p className={`
                                            line-clamp-2 text-sm text-gray-600
                                          `}
                                          >
                                            {qa.answer}
                                          </p>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Chip size="sm" color={getTypeColor(qa.type)} variant="flat">
                                          {qa.type}
                                        </Chip>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-1">
                                          <Calendar className={`
                                            h-3 w-3 text-gray-400
                                          `}
                                          />
                                          <span className={`
                                            text-xs text-gray-500
                                          `}
                                          >
                                            {formatDate(qa.updated_at)}
                                          </span>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            isIconOnly
                                            size="sm"
                                            variant="light"
                                            onPress={() => openEditModal(qa)}
                                            isDisabled={updateMutation.isPending}
                                          >
                                            <Edit3 className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            isIconOnly
                                            size="sm"
                                            variant="light"
                                            color="danger"
                                            onPress={() => handleDeleteSuggestion(qa)}
                                            isDisabled={deleteMutation.isPending}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                    </div>
                  </>
                )}
        </div>
      </div>

      {/* Add Suggestion Modal */}
      <SuggestionModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        mode="add"
        suggestionData={newSuggestion}
        setSuggestionData={setNewSuggestion}
        onSave={handleAddSuggestion}
        isLoading={createMutation.isPending}
        selectedDocument={selectedDocument}
      />

      {/* Edit Suggestion Modal */}
      <SuggestionModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        mode="edit"
        suggestionData={editingSuggestion || { question: '', answer: '', type: 'SUGGESTION' }}
        setSuggestionData={setEditingSuggestion}
        onSave={handleEditSuggestion}
        isLoading={updateMutation.isPending}
        selectedDocument={selectedDocument}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="md" radius="none">
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <p>Are you sure you want to delete this suggestion?</p>
              {deleteConfirm && (
                <div className="rounded border bg-gray-50 p-3">
                  <p className="text-sm font-medium">{deleteConfirm.question}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-gray-600">{deleteConfirm.answer}</p>
                </div>
              )}
              <p className="text-sm text-red-600">This action cannot be undone.</p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onDeleteClose} radius="none">
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={confirmDelete}
              isLoading={deleteMutation.isPending}
              radius="none"
            >
              Delete Suggestion
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
